import React from "react";
import { useNavigate } from "react-router-dom";

import { FormattedNumberInput } from "@features/real-estate/components/formatted-number-input";
import { RealEstateToggle } from "@features/real-estate/components/toggle";
import { REAL_ESTATE_LINE_ITEM_CALC_TYPES, REAL_ESTATE_LINE_ITEM_SECTIONS } from "@features/real-estate/state/constants";
import { useRealEstateWorkspace } from "@features/real-estate/state/workspace";
import type { RealEstateScenario } from "@features/real-estate/types";

const FINANCING_FIELDS: Array<{ key: keyof RealEstateScenario["financing"]; label: string; step: string; kind: "money" | "number"; }> = [
  { key: "purchasePrice", label: "Purchase price", step: "1000", kind: "money" },
  { key: "downPaymentPercent", label: "Down payment %", step: "0.1", kind: "number" },
  { key: "interestRate", label: "Interest rate %", step: "0.01", kind: "number" },
  { key: "amortizationYears", label: "Amortization years", step: "1", kind: "number" },
];

const INCOME_FIELDS: Array<{ key: keyof RealEstateScenario["income"]; label: string; step: string; kind: "money" | "number"; }> = [
  { key: "monthlyRent", label: "Monthly rent", step: "50", kind: "money" },
  { key: "otherMonthlyIncome", label: "Other monthly income", step: "50", kind: "money" },
  { key: "vacancyRate", label: "Vacancy %", step: "0.1", kind: "number" },
  { key: "annualRentGrowthRate", label: "Rent growth %", step: "0.1", kind: "number" },
];

const OPERATING_FIELDS: Array<{ key: keyof RealEstateScenario["operatingExpenses"]; label: string; step: string; kind: "money" | "number"; }> = [
  { key: "annualPropertyTax", label: "Annual property tax", step: "100", kind: "money" },
  { key: "annualInsurance", label: "Annual insurance", step: "100", kind: "money" },
  { key: "propertyManagementPercent", label: "Property management %", step: "0.1", kind: "number" },
  { key: "maintenancePercent", label: "Maintenance %", step: "0.1", kind: "number" },
  { key: "annualExpenseGrowthRate", label: "Expense growth %", step: "0.1", kind: "number" },
];

const RESERVE_FIELDS: Array<{ key: keyof RealEstateScenario["reserves"]; label: string; step: string; kind: "money" | "number"; }> = [
  { key: "capitalReservesPercent", label: "Capital reserves %", step: "0.1", kind: "number" },
];

const REHAB_FIELDS: Array<{ key: keyof RealEstateScenario["rehabCapex"]; label: string; step: string; kind: "money" | "number"; }> = [
  { key: "rehabBudget", label: "Rehab budget", step: "500", kind: "money" },
  { key: "rehabContingencyPercent", label: "Rehab contingency %", step: "0.1", kind: "number" },
];

const EXIT_FIELDS: Array<{ key: keyof RealEstateScenario["exitAssumptions"]; label: string; step: string; kind: "money" | "number"; }> = [
  { key: "holdPeriodMonths", label: "Hold period months", step: "1", kind: "number" },
  { key: "appreciationRate", label: "Appreciation %", step: "0.1", kind: "number" },
  { key: "saleCostsPercent", label: "Exit sale costs %", step: "0.1", kind: "number" },
];

export default function RealEstateUnderwriteScreen(): React.ReactElement {
  const { state, dispatch } = useRealEstateWorkspace();
  const navigate = useNavigate();

  return (
    <section className="re-screen">
      <div className="re-screen-header">
        <div>
          <h2>Underwrite scenarios</h2>
          <p>Base is required. Upside and Downside are exact clones of Base until you change them.</p>
        </div>
        <div className="re-action-row">
          <button
            type="button"
            className="re-button"
            disabled={state.scenarios.some((scenario) => scenario.id === "upside")}
            onClick={() => dispatch({ type: "clone_base_scenario", scenarioId: "upside", name: "Upside" })}
          >
            Add Upside
          </button>
          <button
            type="button"
            className="re-button"
            disabled={state.scenarios.some((scenario) => scenario.id === "downside")}
            onClick={() => dispatch({ type: "clone_base_scenario", scenarioId: "downside", name: "Downside" })}
          >
            Add Downside
          </button>
          <button type="button" className="re-button re-button-primary" onClick={() => navigate("/real-estate/results")}>
            View results
          </button>
        </div>
      </div>

      <div className="re-scenario-stack">
        {state.scenarios.map((scenario) => (
          <article key={scenario.id} className="re-panel re-scenario-card">
            <header className="re-scenario-header">
              <div>
                <div className="re-panel-title">Scenario</div>
                <h3>{scenario.name}</h3>
              </div>
            </header>

            <ScenarioFieldSet
              title="Financing"
              fields={FINANCING_FIELDS}
              values={scenario.financing}
              onChange={(field, value) => dispatch({ type: "update_scenario_field", scenarioId: scenario.id, section: "financing", field, value })}
            />
            <ScenarioFieldSet
              title="Income"
              fields={INCOME_FIELDS}
              values={scenario.income}
              onChange={(field, value) => dispatch({ type: "update_scenario_field", scenarioId: scenario.id, section: "income", field, value })}
            />
            <ScenarioFieldSet
              title="Operating expenses"
              fields={OPERATING_FIELDS}
              values={scenario.operatingExpenses}
              onChange={(field, value) => dispatch({ type: "update_scenario_field", scenarioId: scenario.id, section: "operatingExpenses", field, value })}
            />
            <ScenarioFieldSet
              title="Reserves"
              fields={RESERVE_FIELDS}
              values={scenario.reserves}
              onChange={(field, value) => dispatch({ type: "update_scenario_field", scenarioId: scenario.id, section: "reserves", field, value })}
            />
            <ScenarioFieldSet
              title="Rehab and capex"
              fields={REHAB_FIELDS}
              values={scenario.rehabCapex}
              onChange={(field, value) => dispatch({ type: "update_scenario_field", scenarioId: scenario.id, section: "rehabCapex", field, value })}
            />
            <ScenarioFieldSet
              title="Exit assumptions"
              fields={EXIT_FIELDS}
              values={scenario.exitAssumptions}
              onChange={(field, value) => dispatch({ type: "update_scenario_field", scenarioId: scenario.id, section: "exitAssumptions", field, value })}
            />

            <div className="re-line-item-stack">
              {REAL_ESTATE_LINE_ITEM_SECTIONS.map((section) => (
                <section key={`${scenario.id}-${section.value}`} className="re-line-item-group">
                  <div className="re-line-item-group-header">
                    <div>
                      <h4>{section.label}</h4>
                      <p>Editable fees and cost rules for this section.</p>
                    </div>
                    <button
                      type="button"
                      className="re-button re-button-small"
                      onClick={() => dispatch({ type: "add_line_item", scenarioId: scenario.id, section: section.value })}
                    >
                      Add item
                    </button>
                  </div>

                  <div className="re-line-item-list">
                    {scenario.lineItems.filter((item) => item.section === section.value).map((item) => (
                      <div key={item.id} className="re-line-item-row">
                        <label>
                          <span>Label</span>
                          <input
                            type="text"
                            value={item.label}
                            onChange={(event) => dispatch({
                              type: "update_line_item",
                              scenarioId: scenario.id,
                              lineItemId: item.id,
                              patch: { label: event.target.value },
                            })}
                          />
                        </label>
                        <label className="re-select-field">
                          <span>Calc type</span>
                          <select
                            value={item.calcType}
                            onChange={(event) => dispatch({
                              type: "update_line_item",
                              scenarioId: scenario.id,
                              lineItemId: item.id,
                              patch: { calcType: event.target.value as typeof item.calcType },
                            })}
                          >
                            {REAL_ESTATE_LINE_ITEM_CALC_TYPES.map((option) => (
                              <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                          </select>
                        </label>
                        <label>
                          <span>Amount</span>
                          {isDollarCalcType(item.calcType) ? (
                            <FormattedNumberInput
                              value={item.amount}
                              allowDecimal
                              onValueChange={(value) => dispatch({
                                type: "update_line_item",
                                scenarioId: scenario.id,
                                lineItemId: item.id,
                                patch: { amount: value },
                              })}
                            />
                          ) : (
                            <input
                              type="number"
                              min="0"
                              step="0.1"
                              value={item.amount || ""}
                              onChange={(event) => dispatch({
                                type: "update_line_item",
                                scenarioId: scenario.id,
                                lineItemId: item.id,
                                patch: { amount: event.target.value === "" ? 0 : Number(event.target.value) },
                              })}
                            />
                          )}
                        </label>
                        <div className="re-toggle-field re-toggle-field-compact">
                          <span>Enabled</span>
                          <RealEstateToggle
                            checked={item.enabled}
                            onChange={(checked) => dispatch({
                              type: "update_line_item",
                              scenarioId: scenario.id,
                              lineItemId: item.id,
                              patch: { enabled: checked },
                            })}
                            onLabel="On"
                            offLabel="Off"
                          />
                        </div>
                        {item.removable ? (
                          <button
                            type="button"
                            className="re-button re-button-danger re-button-small"
                            onClick={() => dispatch({ type: "remove_line_item", scenarioId: scenario.id, lineItemId: item.id })}
                          >
                            Remove
                          </button>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

type ScenarioFieldSetProps<T extends Record<string, number>> = {
  title: string;
  fields: Array<{ key: keyof T; label: string; step: string; kind: "money" | "number"; }>;
  values: T;
  onChange: (field: string, value: number) => void;
};

function ScenarioFieldSet<T extends Record<string, number>>({
  title,
  fields,
  values,
  onChange,
}: ScenarioFieldSetProps<T>): React.ReactElement {
  return (
    <section className="re-fieldset">
      <div className="re-panel-title">{title}</div>
      <div className="re-form-grid">
        {fields.map((field) => (
          <label key={`${title}-${String(field.key)}`}>
            <span>{field.label}</span>
            {field.kind === "money" ? (
              <FormattedNumberInput
                value={values[field.key]}
                onValueChange={(value) => onChange(String(field.key), value)}
              />
            ) : (
              <input
                type="number"
                min="0"
                step={field.step}
                value={values[field.key] || ""}
                onChange={(event) => onChange(String(field.key), event.target.value === "" ? 0 : Number(event.target.value))}
              />
            )}
          </label>
        ))}
      </div>
    </section>
  );
}

function isDollarCalcType(calcType: string): boolean {
  return calcType === "fixed_annual" || calcType === "fixed_monthly" || calcType === "one_time";
}
